// A small global store (useSyncExternalStore). The prototype has no server; every interaction
// changes this state, and the URL hash can preset it (used for review links and screenshots).

import { useSyncExternalStore } from 'react';
import {
  initialChats,
  initialPosts,
  initialRequests,
  guestChats,
  feedReplies,
  type Chat,
  type ChatRequest,
  type FeedReply,
  type Message,
  type Post,
  type PersonId,
} from '../data/mock';

export type Layout = 'phone' | 'duoOuter' | 'duoInner' | 'duoSplit' | 'desktopMac' | 'desktopWin';
export type Mode = 'light' | 'dark';
export type Tab = 'chats' | 'feed' | 'calls' | 'you';
export type SettingsPage =
  | 'profile'
  | 'privacy'
  | 'notifications'
  | 'devices'
  | 'appearance'
  | 'recovery'
  | 'chats'
  | 'help';

export type Route =
  | { name: 'chat'; id: string }
  | { name: 'chatInfo'; id: string }
  | { name: 'requests' }
  | { name: 'newChat' }
  | { name: 'post'; id: string }
  | { name: 'settings'; page: SettingsPage };

export type AuthStage =
  | 'welcome'
  | 'email'
  | 'code'
  | 'phone'
  | 'sms'
  | 'terms'
  | 'profile'
  | 'unlock'
  | 'branding'
  | 'in';

/** What the user entered during onboarding (spec section 5). */
export interface Onboarding {
  phone: string;
  terms: boolean;
  privacy: boolean;
  name: string;
  title: string;
  photo: boolean;
  unlock: 'biometric' | 'passcode' | null;
}

export const emptyOnboarding: Onboarding = {
  phone: '',
  terms: false,
  privacy: false,
  name: '',
  title: '',
  photo: false,
  unlock: null,
};

export interface CallState {
  phase: 'incoming' | 'outgoing' | 'active';
  with: PersonId[];
  groupName?: string;
  video: boolean;
  muted: boolean;
  cameraOff: boolean;
  speaker: boolean;
  startedAt: number;
  minimized: boolean;
}

export interface ChatUi {
  menuFor?: string;
  attachOpen?: boolean;
  recording?: boolean;
  recordStart?: number;
  replyTo?: string;
  playing?: string;
}

export interface State {
  layout: Layout;
  mode: Mode;
  landscape: boolean;
  split: 'half' | 'twoThirds' | 'third';
  /** Organisation theme id, or null for users without an organisation. */
  orgId: string | null;
  customAccent: string | null;
  auth: { stage: AuthStage; email: string };
  onboarding: Onboarding;
  tab: Tab;
  stacks: Record<Tab, Route[]>;
  feedTab: 'forYou' | 'following';
  call: CallState | null;
  composeOpen: boolean;
  chatUi: Record<string, ChatUi>;
  drafts: Record<string, string>;
  chats: Chat[];
  guestChats: Chat[];
  requests: ChatRequest[];
  posts: Post[];
  replies: FeedReply[];
  toast: string | null;
  privacy: { readReceipts: boolean; typing: boolean; lastSeen: boolean };
  recoverySaved: boolean;
}

export const initialState: State = {
  layout: 'phone',
  mode: 'light',
  landscape: false,
  split: 'half',
  orgId: 'nordwind',
  customAccent: null,
  auth: { stage: 'in', email: 'anna.berg@nordwind-logistik.de' },
  onboarding: { ...emptyOnboarding, terms: true, privacy: true, unlock: 'biometric' },
  tab: 'chats',
  stacks: { chats: [], feed: [], calls: [], you: [] },
  feedTab: 'forYou',
  call: null,
  composeOpen: false,
  chatUi: {},
  drafts: {},
  chats: initialChats,
  guestChats,
  requests: initialRequests,
  posts: initialPosts,
  replies: feedReplies,
  toast: null,
  privacy: { readReceipts: true, typing: true, lastSeen: false },
  recoverySaved: false,
};

let state: State = initialState;
const listeners = new Set<() => void>();

export function getState(): State {
  return state;
}

export function setState(update: Partial<State> | ((s: State) => Partial<State>)): void {
  const patch = typeof update === 'function' ? update(state) : update;
  state = { ...state, ...patch };
  listeners.forEach((l) => l());
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function useStore<T>(selector: (s: State) => T): T {
  return useSyncExternalStore(subscribe, () => selector(state));
}

// ---------------------------------------------------------------------------------------------
// Navigation

export function push(route: Route, tab?: Tab): void {
  setState((s) => {
    const t = tab ?? s.tab;
    return { tab: t, stacks: { ...s.stacks, [t]: [...s.stacks[t], route] } };
  });
}

/** Open a detail route. In two-pane layouts the detail pane shows one route at a time. */
export function openDetail(route: Route, tab?: Tab): void {
  setState((s) => {
    const t = tab ?? s.tab;
    return { tab: t, stacks: { ...s.stacks, [t]: [route] } };
  });
}

export function pop(): void {
  setState((s) => ({ stacks: { ...s.stacks, [s.tab]: s.stacks[s.tab].slice(0, -1) } }));
}

export function selectTab(tab: Tab): void {
  setState((s) =>
    // Tapping the active tab again returns to its root, as on iOS and Android.
    s.tab === tab ? { stacks: { ...s.stacks, [tab]: [] } } : { tab },
  );
}

// ---------------------------------------------------------------------------------------------
// Chats

export function chatList(s: State): Chat[] {
  return s.orgId ? s.chats : s.guestChats;
}

export function updateChat(id: string, fn: (c: Chat) => Chat): void {
  setState((s) => ({
    chats: s.chats.map((c) => (c.id === id ? fn(c) : c)),
    guestChats: s.guestChats.map((c) => (c.id === id ? fn(c) : c)),
  }));
}

export function setChatUi(id: string, patch: Partial<ChatUi>): void {
  setState((s) => ({ chatUi: { ...s.chatUi, [id]: { ...s.chatUi[id], ...patch } } }));
}

let counter = 0;
const now = () => {
  const d = new Date();
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
};

/** Sends a message and walks it through the receipt states (spec section 7). */
export function sendMessage(chatId: string, message: Omit<Message, 'id' | 'from' | 'time'>): void {
  const id = `local-${++counter}`;
  updateChat(chatId, (c) => ({
    ...c,
    unread: 0,
    messages: [...c.messages, { ...message, id, from: 'me', time: now(), status: 'sending' }],
  }));
  const advance = (status: Message['status'], delay: number) =>
    setTimeout(
      () =>
        updateChat(chatId, (c) => ({
          ...c,
          messages: c.messages.map((m) => (m.id === id ? { ...m, status } : m)),
        })),
      delay,
    );
  advance('sent', 400);
  advance('delivered', 1100);
  if (getState().privacy.readReceipts) advance('read', 2600);
}

export function toggleReaction(chatId: string, messageId: string, emoji: string): void {
  updateChat(chatId, (c) => ({
    ...c,
    messages: c.messages.map((m) => {
      if (m.id !== messageId) return m;
      const reactions = (m.reactions ?? []).map((r) => ({
        ...r,
        by: r.by.filter((p) => p !== 'me'),
      }));
      const had = m.reactions?.find((r) => r.emoji === emoji)?.by.includes('me');
      const existing = reactions.find((r) => r.emoji === emoji);
      if (!had) {
        if (existing) existing.by.push('me');
        else reactions.push({ emoji, by: ['me'] });
      }
      return { ...m, reactions: reactions.filter((r) => r.by.length > 0) };
    }),
  }));
}

export function acceptRequest(id: string): void {
  const s = getState();
  const req = s.requests.find((r) => r.id === id);
  if (!req) return;
  const chat: Chat = {
    id: `req-${req.from}`,
    kind: 'direct',
    members: ['me', req.from],
    messages: [
      {
        id: `${id}-m`,
        from: req.from,
        day: 'Today',
        time: req.time,
        kind: 'text',
        text: req.preview,
      },
    ],
  };
  setState({ requests: s.requests.filter((r) => r.id !== id), chats: [chat, ...s.chats] });
  openDetail({ name: 'chat', id: chat.id }, 'chats');
  toast('Request accepted');
}

export function declineRequest(id: string): void {
  setState((s) => ({ requests: s.requests.filter((r) => r.id !== id) }));
  toast('Request declined. The sender isn’t notified.');
}

// ---------------------------------------------------------------------------------------------
// Calls

export function startCall(withIds: PersonId[], video: boolean, groupName?: string): void {
  setState({
    call: {
      phase: groupName ? 'active' : 'outgoing',
      with: withIds,
      groupName,
      video,
      muted: false,
      cameraOff: !video,
      speaker: video,
      startedAt: Date.now(),
      minimized: false,
    },
  });
  if (!groupName) {
    setTimeout(() => {
      const c = getState().call;
      if (c?.phase === 'outgoing')
        setState({ call: { ...c, phase: 'active', startedAt: Date.now() } });
    }, 2200);
  }
}

export function incomingCall(): void {
  setState({
    call: {
      phase: 'incoming',
      with: ['jonas'],
      video: true,
      muted: false,
      cameraOff: false,
      speaker: true,
      startedAt: Date.now(),
      minimized: false,
    },
  });
}

export function updateCall(patch: Partial<CallState>): void {
  setState((s) => (s.call ? { call: { ...s.call, ...patch } } : {}));
}

export function endCall(): void {
  setState({ call: null });
}

// ---------------------------------------------------------------------------------------------
// Feed

export function updatePost(id: string, fn: (p: Post) => Post): void {
  setState((s) => ({ posts: s.posts.map((p) => (p.id === id ? fn(p) : p)) }));
}

export function addPost(post: Omit<Post, 'id'>): void {
  setState((s) => ({ posts: [{ ...post, id: `p-${++counter}` }, ...s.posts], composeOpen: false }));
  toast('Posted to Nordwind Logistik');
}

export function addReply(postId: string, text: string): void {
  setState((s) => ({
    replies: [
      ...s.replies,
      { id: `r-${++counter}`, postId, author: 'me', time: 'now', text, likes: 0 },
    ],
    posts: s.posts.map((p) => (p.id === postId ? { ...p, replies: p.replies + 1 } : p)),
  }));
}

// ---------------------------------------------------------------------------------------------

let toastTimer: ReturnType<typeof setTimeout> | undefined;
export function toast(message: string): void {
  clearTimeout(toastTimer);
  setState({ toast: message });
  toastTimer = setTimeout(() => setState({ toast: null }), 2600);
}

export function signOut(): void {
  setState({
    ...initialState,
    layout: state.layout,
    mode: state.mode,
    landscape: state.landscape,
    split: state.split,
    orgId: null,
    auth: { stage: 'welcome', email: '' },
    onboarding: emptyOnboarding,
  });
}

export function setOnboarding(patch: Partial<Onboarding>): void {
  setState((s) => ({ onboarding: { ...s.onboarding, ...patch } }));
}
